/*
 * Copyright (c) 2024 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import React, { Suspense, useContext, useEffect, useState } from "react";
import { AccountContext, CaptivePortalContext, ContextUpdater, LoginContext } from "../../context/ContextProvider";
import { getDashboardImageUrl } from "../../util/S3Helper";
import { gridRowDefaultHeight } from "./LayoutPortalUtil";
import { enableBodyScroll } from "body-scroll-lock";
import { contrastingForegroundColor } from "../../util/Color";
import { Input } from "reactstrap";
import { splitIntoSegments } from "../../util/Array";
import {
  defaultTermsOfService,
  defaultTosButtonBackgroundColor,
  defaultTosTextColor,
} from "../loginPage/createLoginPage/loginPageSubsteps/TermsAndConditionsSubstep";
import { requestNetworkEmailCodeResend, requestNetworkGuestUpdateCache, requestNetworkSmsCodeResend } from "../../context/RequestManager";
import { loginLocale as L, loginLocaleFormat as LF, getLoginLocalization, supportedLocalizationOptions } from "../../util/Localization";
import useUpdateState from "../../util/useUpdateState";
import debug from "../../util/constants/Debug";
import countryList from "../../util/CountryList";
import ActionButtonGroup from "../../elements/ActionButtonGroup";

const CenterModal = React.lazy(() => import("../../elements/CenterModal"));
const AppleLoginButton = React.lazy(() => import("../../login/social/AppleLoginButton"));
const FacebookLoginButton = React.lazy(() => import("../../login/social/FacebookLoginButton"));
const GoogleLoginButton = React.lazy(() => import("../../login/social/GoogleLoginButton"));
const LinkedInLoginButton = React.lazy(() => import("../../login/social/LinkedInLoginButton"));
const TwitterLoginButton = React.lazy(() => import("../../login/social/TwitterLoginButton"));
const SocialButtonGraphic = React.lazy(() => import("../../login/social/SocialButtonGraphic"));
const socialButtonConfig = React.lazy(() => import("../../login/social/SocialButtonConfig"));

// helper constants
const startYear = 1900;
const endYear = new Date().getFullYear();
const monthName = [ "january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december" ];
const dayPerMonth = [ 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ];

// helper functions
const getSelectedStyle = (selected) => {
  return selected ? {
    backgroundColor: "#F064",
    border: "2px dashed #F06"
  } : {
    border: "2px solid #0000"
  };
};

const getInnerSelectedStyle = (selected) => {
  return selected ? {
    backgroundColor: "#3F36"
  } : {};
};

const getScalingFactor = (rowHeight, mini = false) => {
  const rowScaler = 0.95;    // ratio of text size based on row height
  const staticScaler = 0.05; // ratio of text size is maintained independent of row height

  return (
    (rowScaler * (rowHeight ?? gridRowDefaultHeight)) +
    (staticScaler * gridRowDefaultHeight)
  ) / gridRowDefaultHeight;
};

const getTextSize = (textSize, rowHeight = null, mini = false) => {
  // look up base size value
  let baseSize;
  switch (textSize) {
    case "xs": baseSize = 12; break;
    case "sm": baseSize = 16; break;
    case "md": baseSize = 20; break;
    case "lg": baseSize = 28; break;
    case "xl": baseSize = 48; break;
    default: baseSize = 1.0;
  }

  // calculate size multiplier on base size values
  const sizeMultiplier = getScalingFactor(rowHeight, mini);

  // return base * multiplier
  return `${baseSize * sizeMultiplier}px`;
};

const getSocialIconSize = (rowHeight, mini = false) => {
  const baseSize = 28;
  const sizeMultiplier = getScalingFactor(rowHeight, mini);
  return Math.floor(baseSize * sizeMultiplier);
}

const getAlignmentStyle = (align) => {
  if (!align) {
    return;
  }

  let horizontalStyle = "";
  let verticalStyle = "";

  try {
    const alignSplit = align.split(" ");

    // horizontal
    if (alignSplit.length === 1) {
      switch (alignSplit[0]) {
        case "left": horizontalStyle = "flex-start"; break;
        case "center": horizontalStyle = "center"; break;
        case "right": horizontalStyle = "flex-end"; break;
        default: horizontalStyle = "center"; break;
      }

      return {
        display: "flex",
        justifyContent: horizontalStyle,
      };
    }

    // vertical and horizontal
    else if (alignSplit.length === 2) {
      // vertical
      switch (alignSplit[0]) {
        case "top": verticalStyle = "flex-start"; break;
        case "middle": verticalStyle = "center"; break;
        case "bottom": verticalStyle = "flex-end"; break;
        default: verticalStyle = "center"; break;
      }

      // horizontal
      switch (alignSplit[1]) {
        case "left": horizontalStyle = "flex-start"; break;
        case "center": horizontalStyle = "center"; break;
        case "right": horizontalStyle = "flex-end"; break;
        default: horizontalStyle = "center"; break;
      }

      return {
        display: "flex",
        justifyContent: horizontalStyle,
        alignItems: verticalStyle,
      };
    }
  } catch (error) {
    debug.warn("Error parsing style")
    return {};
  }

  return {};
};

const textAttributesToStyle = ({
  style = {},
  size = "md",
  align = "left",
  color = "#000",
  rowHeight,
  mini = false
}) => {
  const textDecoration = style.underline ? `underline ${color}` : "none";
  return {
    fontWeight: style.bold ? "bold" : "normal",
    fontStyle: style.italic ? "italic" : "normal",
    textDecoration: textDecoration,
    WebkitTextDecoration: textDecoration,
    fontSize: getTextSize(size, rowHeight, mini),
    textAlign: align,
    color: color,
  };
};

// helper elements
const GridElement = ({children, className, containerStyle, tag}) => {
  return (
    <div id={tag} className={className} style={containerStyle}>
      {children}
    </div>
  );
}

const GridTextContainer = (props) => {
  const { innerStyle, align, children } = props;

  const style = {
    width: "100%",
    height: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    overflowWrap: "anywhere",
    overflow: "hidden",
    ...getAlignmentStyle(align),
    ...innerStyle
  }

  return (
    <GridElement {...props}>
      <div style={style}>
        {children}
      </div>
    </GridElement>
  );
};

// elements
const GridLabel = (props) => {
  const {
    text,
    font,
  } = props;

  return (
    <GridTextContainer {...props}>
      <span style={{
        ...textAttributesToStyle(props),
        fontFamily: font
      }}>
        {text ?? ""}
      </span>
    </GridTextContainer>
  );
};

const GridTitleBodyLabel = (props) => {
  const {
    text,
    bodyText,
    bodySize,
    font,
  } = props;
  const containerProps = {...props, containerStyle: { ...props.containerStyle, backgroundConfigToStyle: "#FF0000"}};
  return (
    <GridTextContainer {...containerProps}>
        <span style={{
          ...textAttributesToStyle(props),
          fontFamily: font
        }}>
          <span>{text ?? ""}</span>
          <pre style={{
            ...textAttributesToStyle({...props, size: bodySize}),
            fontFamily: font
          }}>
            <span>{bodyText ?? ""}</span>
          </pre>
        </span>
    </GridTextContainer>
  );
};

const GridPrefixSuffixLabel = (props) => {
  const {
    prefix,
    text,
    font,
    placeholder,
    suffix,
    show = true,
  } = props;

  return (
    <GridTextContainer {...props}>
      {show && (
        <span style={{
          ...textAttributesToStyle(props),
          fontFamily: font
        }}>
          {prefix && (prefix + ' ')}
          {text ? (
            <span>{text}</span>
          ) : (
            <span className="fw-light text-muted">{placeholder}</span>
          )}
          {suffix && (' ' + suffix)}
        </span>
      )}
    </GridTextContainer>
  );
};

const GridImage = (props) => {
  const {
    url,
    backupUrl,
    size,
    maxWidth,
    selected = false,
    rowHeight,
    preview,
    mini,
  } = props;

  // image url
  const imageUrl = url ?? backupUrl;
  const maxSize = isNaN(size) ? "100%" : `${Math.floor(size)}%`;

  // styles
  const imageStyle = {
    maxWidth: maxWidth ? `min(${maxWidth}px,${maxSize}})` : maxSize,
    maxHeight: maxSize,
    minHeight: maxSize,
    ...getInnerSelectedStyle(selected),
  };

  const textStyle = textAttributesToStyle({
    style: {},
    size: "md",
    align: "center",
    color: "#000000",
    rowHeight: rowHeight,
    mini: mini,
  });

  // handle no image
  return (
    <GridElement {...props}>
      {imageUrl ? (
        <img src={imageUrl} alt="Placeholder" style={imageStyle} />
      ) : preview && (
        <div className="fill-container d-flex align-items-center image-placeholder">
          <span className="w-100 text-center" style={textStyle}>{L("image-placeholder")}</span>
        </div>
      )}
    </GridElement>
  );
};

const GridLoginFields = (props) => {
  const {
    loginMethod,
    boxAlign,
    containerStyle,
    innerStyle,
    fullPreview,
    preview,
    mini,
  } = props;

  // tos modal state
  const [tosOpen, setTosOpen] = useState(false);
  const [optInOpen, setOptInOpen] = useState(false);

  // constants
  const methodInfo = {
    button: { name: L("free-wifi"), icon: "wifi", type: "submit" },
    passcode: { name: LF(L("login-method-string"), L("passcode")), icon: "lock-fill", action: "login-passcode" },
    email: { name: LF(L("login-method-string"), L("email")), icon: "envelope-fill", action: "login-email" },
    sms: {  name: LF(L("login-method-string"), L("sms")), icon: "chat-fill", action: "login-sms" },
    google: { name: LF(L("login-method-string"), L("google")), icon: "google", element: GoogleLoginButton },
    apple: { name: LF(L("login-method-string"), L("apple")), icon: "apple", element: AppleLoginButton },
    facebook: { name: LF(L("login-method-string"), L("facebook")), icon: "facebook", element: FacebookLoginButton },
    linkedin: { name: LF(L("login-method-string"), L("linkedin")), icon: "linkedin", element: LinkedInLoginButton },
    twitter: { name: LF(L("login-method-string"), L("twitter")), icon: "twitter-x", element: TwitterLoginButton },
  };

  const innerProps = {
    ...props,
    containerStyle: {
      height: "100%",
      width: "100%",
      ...getAlignmentStyle(boxAlign),
    },
    innerStyle: {
      height: "100%",
      width: innerStyle.width,
      maxWidth: innerStyle.maxWidth,
    },
    placeholderSize: "sm",
    placeholderColor: "#000000",
    preview: preview,
    fullPreview: fullPreview,
    mini: mini,
  };

  // allow scrolling in the login fields  
  useEffect(() => {
    enableBodyScroll(document.getElementById('grid-login-fields'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // context 
  const accountContext = useContext(AccountContext);
  const updateContexts = useContext(ContextUpdater);
  const loginContext = useContext(LoginContext);

  // handle expired session
  if (loginContext.sessionExpired) {
    return (
      <GridLabel {...innerProps} text={L("session-expired")} size="md" align="center" />
    );
  }
  
  // tos and gdpr state
  const {
    loginToken,
    loginUser,
    preloginPreview,
    tosAccepted,
    tosHtml,
    tosTextColor,
    tosButtonBackgroundColor,
    tosButtonTextColor,
    gdprEnabled,
    gdprAccepted,
    gdprFormEnabled,
    gdprForm,
    verificationRequired,
    hasSmsConfig = true,
    hasFacebookConfig = true,
    hasGoogleConfig = true,
    hasTwitterConfig = true,
  } = loginContext;
  const termsOfService = tosHtml ? tosHtml : defaultTermsOfService;
  const optInForm = gdprForm;

  const pendingGdprFeedack = (gdprEnabled || gdprFormEnabled) && gdprAccepted == null;
  const showGdpr = pendingGdprFeedack && (preloginPreview || tosAccepted);
  const thumbnailPreview = Boolean(preview && mini);
  const preloginDone = thumbnailPreview || (!preloginPreview && (preview || (tosAccepted && !pendingGdprFeedack)));

  const showWelcomeBack = loginToken != null;
  const showPrelogin = !showWelcomeBack && !preloginDone;
  const showLogin = !showWelcomeBack && !showPrelogin;

  // const hasSmsConfig = loginContext.hasSmsConfig ?? ((accountContext.clientInfo?.brand?.hasSmsConfig ?? true) || (accountContext.clientInfo?.hasSmsConfig ?? false));
  const acceptBackground = tosButtonBackgroundColor ?? defaultTosButtonBackgroundColor;
  const staticButtonProps = {
    ...innerProps,
    type: "button",
    buttonColor: acceptBackground,
    textSize: "md",
    textAlign: "center",
    textColor: tosButtonTextColor ?? contrastingForegroundColor(acceptBackground, 0.6),
  };

  const newContainerStyle = {
    ...containerStyle,
    ...(!showLogin) && { justifyContent: "start" },
    ...(showLogin) &&  { justifyContent: "flex-start"},
  };

  const gdprFormContainerStyle = {
    width: "100%",
    maxWidth: "600px",
    overflow: "hidden",
  }

  // button state
  const loginType = thumbnailPreview ? null : loginContext.loginType;
  const buttonStyle = loginMethod.buttonStyle;
  const textAlign = loginMethod.textAlign ?? "left";
  const showIcons = buttonStyle === "app" ? true : (loginMethod.showIcons ?? true);
  const passcode = loginMethod.passcode;
  const countryCode = loginMethod.countryCode;
  let methods = loginMethod.methods?.length ? loginMethod.methods.filter((item) => {
    if (item.type === "sms") {
      return hasSmsConfig;
    }
    if (item.type === "facebook") {
      return hasFacebookConfig 
    }
    if (item.type === "google") {
      return hasGoogleConfig 
    }
    if (item.type === "twitter") {
      return hasTwitterConfig 
    }

    return true;
  }) : [{ type: "button", backgroundColor: "#865446" }];
  if (buttonStyle === "app") {
    methods = splitIntoSegments(methods, 3);
  }

  // Flag indicating login page will automatically connect
  const acceptConnect = methods?.length === 1 && (methods?.[0]?.type === "button");
  
  // input fields
  const defaultFields = { email: [ "name", "email" ], sms: [ "name", "sms" ] };
  const inputFields = verificationRequired ? [ "verification" ] : loginMethod.fields?.[loginType]?.map(type => type.type) ?? defaultFields[loginType];
  const inputFieldConfig = verificationRequired ? [ "verification" ] : loginMethod.fields?.[loginType];

  let inputDescription = null;
  if (verificationRequired) {
    switch (loginType) {
      case "email": inputDescription = L("email-verify-instructions"); break;
      case "sms": inputDescription = L("sms-verify-instructions"); break;
      default: break;
    }
  }

  const previewConnectHandler = (fullPreview && !verificationRequired) ? () => loginContext.updateState({ verificationRequired: true }) : null;
  const emailPreviewConnectHandler = loginContext.previewEmailVerify ? previewConnectHandler : null;
  const smsPreviewConnectHandler = loginContext.previewSmsVerify ? previewConnectHandler : null;

  const resendEmailCode = loginContext.previewEmailVerify ? null : () => handleResendCode("email");
  const resendSmsCode = loginContext.previewSmsVerify ? null : () => handleResendCode("sms");

  const configuredLoginMethods = loginMethod.methods ?? [];
  const activeMethodForConnect = loginType
    ? configuredLoginMethods.find((method) => method.type === loginType)
    : null;
  const connectButtonColor =
    activeMethodForConnect?.connectButtonColor ?? "#EE8800";
  const connectTextColor =
    activeMethodForConnect?.connectTextColor ?? "#FFFFFF";
  const connectFieldProps = { connectButtonColor, connectTextColor };

  // handlers
  const handleTosAccepted = () => {
    const finished = () => loginContext.updateState({ tosAccepted: true,  ...(acceptConnect && !pendingGdprFeedack) && { autoConnect: true }});

    if (fullPreview || gdprEnabled) {
      finished();
    } else {
      requestNetworkGuestUpdateCache({
        context: updateContexts,
        identity: loginContext.identity,
        macAddress: loginContext.deviceMac,
        locale: loginContext.localization,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        tosAccepted: true,
        showLoading: true,
        completion: finished
      });
    }
  };

  const handleGdprAccepted = (accepted) => {
    const finished = () => loginContext.updateState({ gdprAccepted: accepted, ...(acceptConnect) && { autoConnect: true }});

    if (fullPreview) {
      finished();
    } else {
      requestNetworkGuestUpdateCache({
        context: updateContexts,
        identity: loginContext.identity,
        macAddress: loginContext.deviceMac,
        locale: loginContext.localization,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        tosAccepted: true,
        gdprAccepted: accepted,
        showLoading: true,
        completion: finished
      });
    }
  };

  const handleCancelConnect = () => {
    loginContext.updateState({ loginToken: null });
    requestNetworkGuestUpdateCache({
      context: updateContexts,
      identity: loginContext.identity,
      macAddress: loginContext.deviceMac,
      locale: loginContext.localization,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      showLoading: true
    });
  };

  const handleResendCode = (type) => {
    const requestProps = {
      context: updateContexts,
      identity: loginContext.identity,
    };
    switch (type) {
      case "email":
        requestNetworkEmailCodeResend({
          ...requestProps,
          email: loginContext.email
        });
        break;
      case "sms":
        requestNetworkSmsCodeResend({
          ...requestProps,
          phoneNumber: loginContext.phoneNumber
        });
        break;
      default: break; // do nothing
    }
  };

  return (
    <Suspense>
    <GridElement tag="grid-login-fields" className="flex-column" {...props} containerStyle={newContainerStyle}>
      <div className="flex-column" style={showLogin ? newContainerStyle : { display: "none" }}>
        {loginType === "passcode" && (
          <GridInputFields
            {...innerProps}
            {...connectFieldProps}
            countryCode={countryCode}
            fields={[ "passcode" ]}
          />
        )}
        {loginType === "email" && (
          <GridInputFields
            {...innerProps}
            {...connectFieldProps}
            description={inputDescription}
            resendCode={verificationRequired}
            countryCode={countryCode}
            fields={inputFields}
            fieldConfig={inputFieldConfig}
            onConnect={emailPreviewConnectHandler}
            onResend={resendEmailCode}
          />
        )}
        {loginType === "sms" && (
          <GridInputFields
            {...innerProps}
            {...connectFieldProps}
            description={inputDescription}
            resendCode={verificationRequired}
            countryCode={countryCode}
            fields={inputFields}
            fieldConfig={inputFieldConfig}
            onConnect={smsPreviewConnectHandler}
            onResend={resendSmsCode}
          />
        )}
        {loginType == null && !(loginContext.autoConnect ?? false) && (
          buttonStyle === "app" ? methods.map((methodSection, i) => (
            <React.Fragment key={i}>
              {i !== 0 && <div className="flex-5" />}
              <div className="flex-25 d-flex justify-content-center">
                {methodSection.map((method, j) => {
                  const { color, backgroundColor } = method;
                  const { type, icon, action, element: Element = GridButton } = methodInfo[method.type] ?? {};
                  return (
                    <React.Fragment key={j}>
                      {j !== 0 && <div className="square-container" style={{ height: "20%" }} />}
                      <div className="square-container d-flex">
                        <Element
                          {...innerProps}
                          type="button"
                          text=""
                          icon={showIcons ? icon : null}
                          action={action}
                          functional={!preview}
                          buttonColor={backgroundColor}
                          buttonType={type}
                          textSize="lg"
                          textAlign="center"
                          textColor={color ?? contrastingForegroundColor(backgroundColor, 0.6)}
                          correctPasscode={passcode}
                          fullPreview={fullPreview}
                        />
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </React.Fragment>
          )) : methods.map((method, index) => {
            const { color, backgroundColor } = method;
            const {
              name, type, icon, action,
              element: Element = GridButton
            } = methodInfo[method.type] ?? {};
            return (
              <React.Fragment key={index}>
                {index !== 0 && <div className="flex-5" />}
                <div className="flex-16 d-flex">
                  <Element
                    {...innerProps}
                    type="button"
                    text={name}
                    icon={showIcons ? icon : null}
                    action={action}
                    functional={!preview}
                    buttonColor={backgroundColor}
                    buttonType={type}
                    textSize="md"
                    textAlign={textAlign}
                    textColor={color ?? contrastingForegroundColor(backgroundColor, 0.6)}
                    correctPasscode={passcode}
                    fullPreview={fullPreview}
                  />
                </div>
              </React.Fragment>
            );
          }))
        }
      </div>
      <div className="flex-column" style={showPrelogin ? newContainerStyle : { display: "none" }}>
        <div className="flex-15 d-flex">
          <GridButton
            {...staticButtonProps}
            text={(acceptConnect ?? false) ? L("accept-connect") : L("accept")}
            onClick={() => handleTosAccepted()}
            disabled={tosAccepted}
          />
        </div>
        <div className="flex-2" />
        <div className="flex-5">
          <GridTermsOfServiceLabel {...innerProps} onClick={() => setTosOpen(true)} />
        </div>
        {showGdpr && (
          <>
            <div className="flex-10" />
            <div className="flex-5">
              <GridOptInFormLabel {...innerProps} onClick={() => setOptInOpen(true)} />
            </div>            
            <div className="flex-10" />
            <div className="flex-15 d-flex">
              <GridButton {...staticButtonProps} text={L("yes")} onClick={() => handleGdprAccepted(true)} />
            </div>
            <div className="flex-2" />
            <div className="flex-15 d-flex">
              <GridButton {...staticButtonProps} text={L("no")} onClick={() => handleGdprAccepted(false)} />
            </div>
          </>
        )}
      </div>
      <div className="flex-column" style={showWelcomeBack ? newContainerStyle : { display: "none" }}>
        <div className="flex-15 d-flex">
          <GridLabel
            {...innerProps}
            text={loginUser?.display ? LF(L("welcome-back-name"), loginUser.display) : L("welcome-back")}
            size="md"
            align="center"
            color={tosUsesCustomTextColor(tosTextColor) ? tosTextColor : undefined}
          />
        </div>
        <div className="flex-5" />
        <div className="flex-25">
          <GridConnectingAnimation
            {...innerProps}
            color={tosUsesCustomTextColor(tosTextColor) ? tosTextColor : undefined}
          />
        </div>
        <div className="flex-10" />
        <div className="flex-10 d-flex">
          <GridLabel
            {...innerProps}
            text={L("connecting-you-to-wifi")}
            size="xs"
            align="center"
            color={tosUsesCustomTextColor(tosTextColor) ? tosTextColor : undefined}
          />
        </div>
        <div className="flex-20" />
        <div className="flex-10">
          <GridButton
            {...innerProps}
            type="text"
            text={L("ellipsis-not-you")}
            textSize="sm"
            textColor={tosUsesCustomTextColor(tosTextColor) ? tosTextColor : undefined}
            textStyle={{ underline: true }}
            onClick={handleCancelConnect}
          />
        </div>
      </div>
      <CenterModal isOpen={tosOpen} toggle={() => setTosOpen(prev => !prev)} size="lg">
        <div className="d-flex justify-content-end">
          <ActionButtonGroup
            className="ps-2 mb-2"
            closeHandler={() => setTosOpen(prev => !prev)}
          />
        </div>
        
        <div
          style={!tosHtml && tosUsesCustomTextColor(tosTextColor) ? { color: tosTextColor } : undefined}
          dangerouslySetInnerHTML={{ __html: termsOfService }}
        />
      </CenterModal>
      <CenterModal isOpen={optInOpen} toggle={() => setOptInOpen(prev => !prev)} size="lg">
        <div className="d-flex justify-content-end">
          <ActionButtonGroup
            className="ps-2 mb-2"
            closeHandler={() => setOptInOpen(prev => !prev)}
          />
        </div>
        {optInForm ? (
          <>
            <div className="flex-2" />
            <div className="flex-35 container" style={gdprFormContainerStyle}>
                <div dangerouslySetInnerHTML={{__html: optInForm }} />
            </div>
            <div className="flex-2" />
          </>
        ) : (
          <>
            <div className="flex-10" />
            <div className="flex-15">
              <GridLabel
                {...innerProps}
                text={L("gdpr-description")}
                size="sm"
                align="center"
                color={tosUsesCustomTextColor(tosTextColor) ? tosTextColor : undefined}
              />
            </div>
            <div className="flex-10">
              <GridLabel
                {...innerProps}
                text={L("gdpr-disclaimer")}
                size="xs"
                align="center"
                color={tosUsesCustomTextColor(tosTextColor) ? tosTextColor : undefined}
              />
            </div>
          </>
        )}
      </CenterModal>
    </GridElement>
    </Suspense>
  );
};

const GridInputFields = (props) => {
  const {
    fields,
    description,
    resendCode = false,
    onConnect,
    onResend,
    innerStyle,
    preview,
    connectButtonColor = "#EE8800",
    connectTextColor = "#FFFFFF",
  } = props;

  const innerProps = {
    ...props
  };

  const availableFields = {
    name: { placeholder: L("name"), icon: "person" },
    email: { placeholder: L("email"), icon: "envelope" },
    sms: { placeholder: L("phone-number"), icon: "telephone" },
    birthday: { placeholder: L("birthday"), icon: "cake2" },
    country: { placeholder: L("country"), icon: "globe2" },
    state: { placeholder: L("state-or-province"), icon: "geo-alt" },
    customTextField: { icon: "person-gear" },
    passcode: { placeholder: L("passcode"), icon: "shield-lock" },
    verification: { placeholder: L("passcode"), icon: "shield-lock" },
  };

  const activeFields = fields
    .filter(field => availableFields[field] != null)
    .map(field => ({ type: field, ...availableFields[field] }));

  return (
    <GridElement {...props}>
      <div className="d-flex flex-column" style={innerStyle}>
        <div
          className="flex-90 d-flex flex-column justify-content-center"
          style={{ backgroundColor: "white", borderRadius: "3vh", padding: "0 5% 0 5%" }}
        >
          {description && (
            <>
              <div className="flex-15">
                <GridLabel {...innerProps} text={description} size="sm" align="center" />
              </div>
              <div className="flex-5" />
            </>
          )}
          {activeFields.map((field, index) => (
            <React.Fragment key={field.type}>
              {index !== 0 && <div className="flex-2" />}
              <div className="flex-15 d-flex">
                <GridTextField {...innerProps} {...field} />
              </div>
            </React.Fragment>
          ))}
          <div className="flex-10" />
          <div className="flex-15 d-flex">
            <GridButton
              {...innerProps}
              buttonType={onConnect == null ? "submit" : "button"}
              text={L("connect")}
              buttonColor={connectButtonColor}
              textColor={connectTextColor}
              onClick={onConnect}
            />
          </div>
          {resendCode && (
            <>
              <div className="flex-5" />
              <div className="flex-15 d-flex">
                <GridButton
                  {...innerProps}
                  buttonType="button"
                  text={L("resend-code")}
                  buttonColor={connectButtonColor}
                  textColor={connectTextColor}
                  onClick={onResend}
                />
              </div>
            </>
          )}
        </div>
        <div className="flex-2" />
        <div className="flex-5 d-flex">
          <GridButton
            {...innerProps}
            type="text"
            textSize="sm"
            icon="arrow-left"
            action="login-back"
            text={L("back")}
            functional={!preview}
          />
        </div>
      </div>
    </GridElement>
  );
};

const GridConnectingAnimation = (props) => {
  const { rowHeight, innerStyle, color } = props;
  const accentColor = color ?? "#000";
  const getIconStyle = (align) => textAttributesToStyle({ size: "xl", align, color: accentColor, rowHeight });

  // animation state
  const lineCount = 14;
  const [lineState, setLineState] = useState({ index: 0, heights: Array(lineCount).fill(false) });
  useEffect(() => {
    const interval = setInterval(() => setLineState(prevState => ({
      index: (prevState.index + 1) % lineCount,
      heights: prevState.heights.map((height, index) => index === prevState.index ? !height : height)
    })), 70);
    return () => clearInterval(interval);
  }, []);

  return (
    <GridElement {...props}>
      <div className="d-flex" style={innerStyle}>
        <div className="flex-20 align-items-center" style={getIconStyle("right")}>
          <div className="fill-container d-flex align-items-center justify-content-end">
            <i className="bi-person" />
          </div>
        </div>
        <div className="flex-60 d-flex justify-content-center align-items-center">
          {lineState.heights.map((big, index) => (
            <React.Fragment key={index}>
              {index !== 0 && <div className="flex-5" />}
              <div
                className="flex-2"
                style={{
                  transition: "all 800ms ease-in-out",
                  backgroundColor: accentColor,
                  height: big ? "60%" : "10%",
                }}
              />
            </React.Fragment>
          ))}
        </div>
        <div className="flex-20 align-items-center" style={getIconStyle("left")}>
          <div className="fill-container d-flex align-items-center justify-content-start">
            <i className="bi-wifi" />
          </div>
        </div>
      </div>
    </GridElement>
  )
};

const GridTextField = (props) => {
  const {
    type,
    icon,
    placeholder,
    placeholderSize,
    placeholderColor,
    backgroundColor,
    defaultCountryCode,
    innerStyle,
    rowHeight,
    preview,
    fullPreview,
    mini,
    fieldConfig
  } = props;
  
  const loginContext = useContext(LoginContext);
  const captivePortalContext = useContext(CaptivePortalContext);
  const disabled = fullPreview ?? preview ?? false;
  const background = fullPreview ? "#D7DCDD" : backgroundColor;

  // birthday state
  const [ birthday,, updateBirthday ] = useUpdateState({
    birthDay: loginContext.birthDay,
    birthMonth: loginContext.birthMonth,
    birthYear: loginContext.birthYear,
  });

  // element props
  const getValue = (type) => { 
    const countryCodeValue = loginContext.phoneNumberCountryCode
                          ?? defaultCountryCode?.replace(/^\+/g, "")
                          ?? captivePortalContext?.config?.fields?.phoneNumberCountryCode
                          ?? "1";
    const countryCode = "+" + countryCodeValue;

    const countryConfig = captivePortalContext?.config?.fields?.country ?? "";
    const country = loginContext.country ?? (countryConfig.length > 0 ? countryConfig : null) ?? "United States";

    // preview
    if (fullPreview) {
      switch (type) {
        case "passcode": return "Passcode";
        case "email": return "sample@email.com";
        case "password": return "Password";
        case "sms": return "Sample Phone Number";
        case "smsCountryCode": return countryCode;
        case "smsPassword": return "Password";
        case "name": return "Sample Name";
        case "country": return "Country";
        case "state": return "State/Province";
        case "birthDay": return 1;
        case "birthMonth": return 1;
        case "birthYear": return endYear;
        case "customTextField": return "Custom Text";
        default: return "";
      }
    }

    // non-preview
    switch (type) {
      case "birthDay":
      case "birthMonth":
      case "birthYear":
        return birthday[type];
      case "smsCountryCode":
        return loginContext.smsCountryCode ?? countryCode;
      case "country":
        return country;
      default:
        return loginContext[type] ?? "";
    }
  };

  const getInputFieldProps = (type) => {
    const value = getValue(type);
    let props = {
      id: `signup-${type}`,
      defaultValue: getValue(type),
      type: "text",

      /* BTM: Leave this here, for some reason it is needed for the Android CNA to work properly */
      name: "username"
    };

    switch (type) {
      case "sms":
        props.inputMode = "tel";
        break;
      case "smsCountryCode":
        props.inputMode = "tel";
        break;
      case "birthDay":
      case "birthMonth":
      case "birthYear":
        props = {
          id: `signup-${type}`,
          value: value,
          name: type,
          onChange: (e) => updateBirthday({ [type]: e.target.value }),
        }
        break;
      case "customTextField":
        props.placeholder = fieldConfig.find(item => item.type === type)?.text;
        props.maxLength = "32";
        props.required = true;
        break;
      case "country":
        props.disabled = false;
        break;
      default:
        // do nothing
        break;
    }
    return { ...props };
  };
  
  const inputProps = (type) => {
    return {
      placeholder: placeholder,
      disabled: disabled,
      style: {
        color: placeholderColor,
        backgroundColor: background ?? "#0000",
        fontSize: getTextSize(placeholderSize, rowHeight, mini),
        outline: "0",
        borderWidth: "0 0 1px",
        borderColor: "#000000",
        ...innerStyle,
      },
      ...getInputFieldProps(type),
    };
  };

  const getInput = (type) => {
    const inputType = () => {
      switch (type) {
        case "sms": return (
          <>
            <div className="flex-20">
              <input {...inputProps("smsCountryCode")} placeholder="+" />
            </div>
            <div className="flex-75">
              <input {...inputProps(type)} />
            </div>
          </>
        );
        case "birthday": return (
          <>
            <div className="flex-20">
              <select {...inputProps("birthDay")}>
                {Array.from({ length: dayPerMonth[birthday.birthMonth - 1] }, (_, index) => (index + 1).toString()).map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="flex-45">
              <select {...inputProps("birthMonth")}>
                {monthName.map((month, index) => (
                  <option key={index} value={(index + 1).toString()}>{L(month)}</option>
                ))}
              </select>
            </div>
            <div className="flex-35">
              <select {...inputProps("birthYear")}>
                {Array.from({ length: endYear - startYear + 1 }, (_, index) => (index + startYear).toString()).reverse().map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
          </>
        );
        case "country": return (
          <>
            <div className="w-100">
              <select {...inputProps(type)}>
                {fullPreview && <option id={"Country"} value={"Country"}>{L("country") ?? "Country"}</option>}
                {countryList.map((country) => (<option id={country} value={country}>{L(country) ?? country}</option>))}
              </select>
            </div>
          </>
        )
        default: return <input {...inputProps(type)} />;
      }
    }

    return (
      <div className="flex-dynamic d-flex justify-content-between">
        {inputType(type)}
      </div>
    );
  };

  return (
    <GridElement {...props}>
      <div className="d-flex align-items-center" style={{ ...innerStyle, fontSize: getTextSize(placeholderSize, rowHeight, mini)}}>
        {icon && <div className="flex-static" style={{ padding: "0 3% 0 0" }}><i className={`icon bi bi-${icon}`} /></div>}
        {getInput(type)}
      </div>
    </GridElement>
  );
};

export const GridButton = (props) => {
  const {
    type = "button",
    text,
    icon,
    action,
    onClick,
    functional,
    disabled,
    buttonType = "button",
    buttonColor,
    textStyle,
    textSize,
    textAlign = "center",
    textColor,
    correctPasscode,
    url,
    innerStyle,
    rowHeight,
    preview,
    mini
  } = props;
  
  // login context
  const loginContext = useContext(LoginContext);
  const isDisabled = disabled != null ? disabled : (preview ?? false);

  // get click action
  let clickHandler = onClick
  let forcedText = null;

  if (!clickHandler) {
    switch (action) {
      case "redirect":
        clickHandler = () => window.open(url, "_blank");
        break;
      case "signup":
        forcedText = L("signup");
        clickHandler = functional ? () => loginContext.updateState({ page: "signup" , registerPhoneNumber: null }) : null;
        break;
      case "login-passcode":
        clickHandler = () => loginContext.updateState({ loginType: "passcode", correctPasscode: correctPasscode });
        break;
      case "login-email":
        clickHandler = () => loginContext.updateState({ loginType: "email" });
        break;
      case "login-sms":
        clickHandler = () => loginContext.updateState({ loginType: "sms" });
        break;
      case "login-back":
        clickHandler = () => loginContext.updateState({ loginType: null, verificationRequired: false });
        break;
      default: break;
    }
  }

  // styles
  const paddingR = { padding: "0 2% 0 0" };
  const paddingX = { padding: "0 2%" };
  const buttonStyle = {
    ...innerStyle,
    ...textAttributesToStyle({
      style: textStyle,
      size: textSize,
      align: textAlign,
      color: textColor,
      rowHeight: rowHeight,
      mini: mini,
    }),
    opacity: disabled ? (type === "text" ? "50%" : "65%") : "100%",
    cursor: disabled ? "default" : "pointer",
    padding: "0 2%",
    width: "100%"
  };

  // text
  const shownText = forcedText ?? text;

  return (
    <GridElement {...props}>
      {type === "button" && (
        <button
          type={buttonType}
          onClick={() => !isDisabled && clickHandler?.()}
          style={{
            ...buttonStyle,
            backgroundColor: buttonColor,
            borderRadius: "1.5vmin",
            border: "none",
          }}
        >
          {icon && <i className={`icon bi bi-${icon}`} style={shownText ? paddingX : {}} />}
          {shownText && <span style={paddingX}>{shownText}</span>}
        </button>
      )}
      {type === "text" && (
        <span
          className="no-select"
          onClick={() => !isDisabled && clickHandler?.()}
          style={buttonStyle}
        >
          {icon && <i className={`icon bi bi-${icon}`} style={paddingR} />}
          {forcedText ?? text}
        </span>
      )}
    </GridElement>
  );
};

const tosUsesCustomTextColor = (tosTextColor) => {
  if (tosTextColor == null) {
    return false;
  }
  return tosTextColor.toUpperCase() !== defaultTosTextColor;
};

const GridAgreeLabel = ({ prefix, suffix, onClick, labelStyle, tosTextColor }) => {
  if (!tosUsesCustomTextColor(tosTextColor)) {
    return (
      <span style={labelStyle}>
        {prefix}{' '}
        <span className="link-primary" onClick={onClick} style={{ cursor: "pointer" }}>
          {suffix}
        </span>
      </span>
    );
  }

  const colorStyle = { ...labelStyle, color: tosTextColor };
  const linkStyle = {
    color: tosTextColor,
    textDecoration: "underline",
    cursor: "pointer",
  };

  return (
    <span style={colorStyle}>
      {prefix}{' '}
      <span onClick={onClick} style={linkStyle}>
        {suffix}
      </span>
    </span>
  );
};

const GridTermsOfServiceLabel = (props) => {
  const { onClick } = props;
  const loginContext = useContext(LoginContext);
  const labelStyle = textAttributesToStyle({ ...props, size: "sm", align: "center" });

  return (
    <GridTextContainer align="center" {...props}>
      <GridAgreeLabel
        prefix={L("agree-to-tos-prefix")}
        suffix={L("agree-to-tos-suffix")}
        onClick={onClick}
        labelStyle={labelStyle}
        tosTextColor={loginContext.tosTextColor}
      />
    </GridTextContainer>
  );
};

const GridOptInFormLabel = (props) => {
  const { onClick } = props;
  const loginContext = useContext(LoginContext);
  const labelStyle = textAttributesToStyle({ ...props, size: "sm", align: "center" });

  return (
    <GridTextContainer align="center" {...props}>
      <GridAgreeLabel
        prefix={L("agree-to-opt-in-prefix")}
        suffix={L("agree-to-opt-in-suffix")}
        onClick={onClick}
        labelStyle={labelStyle}
        tosTextColor={loginContext.tosTextColor}
      />
    </GridTextContainer>
  );
};

const GridSocialButton = (props) => {
  const {
    type,
    shape,
    textAlign,
    functional,
    innerStyle,
    rowHeight,
    preview,
    mini,
    hasFacebookConfig = null,
  } = props;
  
  // get button element
  let ButtonType = SocialButtonGraphic;

  if (functional) {
    switch (type) {
      case "google": ButtonType = GoogleLoginButton; break;
      case "apple": ButtonType = AppleLoginButton; break;
      case "facebook": ButtonType = FacebookLoginButton; break;
      case "linkedin": ButtonType = LinkedInLoginButton; break;
      case "twitter": ButtonType = TwitterLoginButton; break;
      default:
        debug.warn("Unknown social button type: " + type);
        return null;
    }
  }

  return (
    <GridElement {...props}>
      <ButtonType
        config={socialButtonConfig[type](getSocialIconSize(rowHeight, mini))}
        textSize={getTextSize("md", rowHeight, mini)}
        disabled={preview}
        circle={shape === "circle"}
        style={innerStyle}
        innerStyle={getAlignmentStyle(textAlign)}
      />
    </GridElement>
  );  
};

const GridLocalization = (props) => {
  const {
    backgroundColor,
    innerStyle,
    rowHeight,
    preview,
    mini
  } = props;

  // parse context
  const contextUpdader = useContext(ContextUpdater);
  const loginContext = useContext(LoginContext);
  const currentLocalization = loginContext.localization;

  // change handler
  const handleLocalizationChanged = (event) => {
    const localizationId = event.target.value;

    // update local states
    loginContext.updateState({ localization: localizationId });
    getLoginLocalization().setLanguage(localizationId);

    // request to change guest preference
    requestNetworkGuestUpdateCache({
      context: contextUpdader,
      identity: loginContext.identity,
      macAddress: loginContext.deviceMac,
      locale: localizationId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      completion: () => {}
    })
  };

  return (
    <GridElement {...props}>
      <Input
        type="select"
        value={currentLocalization}
        onChange={handleLocalizationChanged}
        disabled={preview}
        style={{
          padding: "0 2% 0 2%",
          fontSize: getTextSize("xs", rowHeight, mini),
          backgroundColor: backgroundColor,
          ...innerStyle,
        }}
      >
        {supportedLocalizationOptions()}
      </Input>
    </GridElement>
  );
};

const GridRectangle = (props) => {
  const {
    color,
    borderColor,
    innerStyle,
    containerStyle,
    selected
  } = props;

  const overrideContainerStyle = {
    ...containerStyle,
    padding: selected ? "2px" : "0",
    backgroundColor: "none",
  };

  return (
    <GridElement {...props} containerStyle={overrideContainerStyle}>
      <div
        style={{
          ...innerStyle,
          backgroundColor: color,
          border: `1px solid ${borderColor}`,
          borderRadius: "24px"
        }}
      />
    </GridElement>
  );
};

const LayoutPortalElement = ({
  type,
  config = {},
  fields = {},
  rowHeight = gridRowDefaultHeight,
  preview = false,
  fullPreview = false,
  mini = false,
  selected = false
}) => {
  let Element;
  let attributes = {};

  // helper functions
  const parseGridPrefixSuffixLabel = (text, placeholder) => {
    const show = preview || text != null;
    return [ GridPrefixSuffixLabel, { show, text, placeholder } ];
  }

  // pick over element type
  switch (type) {
    case "businessName":
      [Element, attributes] = parseGridPrefixSuffixLabel(fields.businessName, "Business-Name");
      break;
    case "locationName":
      [Element, attributes] = parseGridPrefixSuffixLabel(fields.locationName, "Location-Name");
      break;
    case "routerName":
      [Element, attributes] = parseGridPrefixSuffixLabel(fields.routerName, "Router-Name");
      break;
    case "emailLabel":
      [Element, attributes] = parseGridPrefixSuffixLabel(fields.email, "sample@email.com");
      break;
    case "websiteLabel":
      [Element, attributes] = parseGridPrefixSuffixLabel(fields.website, "https://sample.website.com");
      break;
    case "addressLabel":
      [Element, attributes] = parseGridPrefixSuffixLabel(fields.address, "Address");
      break;
    case "customLabel":
      Element = GridTitleBodyLabel;
      break;
    case "logo":
      Element = GridImage;
      attributes = {
        url: fields.logoUrl,
        backupUrl: getDashboardImageUrl("default_wifi_logo"),
        maxWidth: config.boxMaxWidth
      };
      break;
    case "staticImage":
      Element = GridImage;
      attributes = { url: config.url, maxWidth: config.boxMaxWidth };
      break;
    case "loginFields":
      Element = GridLoginFields;
      break;
    case "usernameField":
      Element = GridTextField;
      attributes = { type: "username" };
      break;
    case "passwordField":
      Element = GridTextField;
      attributes = { type: "password" };
      break;
    case "smsField":
      Element = GridTextField;
      attributes = { type: "sms" };
      break;
    case "smsPasswordField":
      Element = GridTextField;
      attributes = { type: "smsPassword" };
      break;
    case "submitButton":
      Element = GridButton;
      attributes = { buttonType: "submit" };
      break;
    case "redirectButton":
      Element = GridButton;
      attributes = { action: "redirect" };
      break;
    case "signupButton":
      Element = GridButton;
      attributes = { action: "signup", functional: !preview };
      break;
    case "localizationBar":
      Element = GridLocalization;
      break;
    case "rectangle":
      Element = GridRectangle;
      break;
    case "googleLogin":
      Element = GridSocialButton;
      attributes = { type: "google", functional: !preview };
      break;
    case "appleLogin":
      Element = GridSocialButton;
      attributes = { type: "apple", functional: !preview };
      break;
    case "facebookLogin":
      Element = GridSocialButton;
      attributes = { type: "facebook", functional: !preview };
      break;
    case "linkedinLogin":
      Element = GridSocialButton;
      attributes = { type: "linkedin", functional: !preview };
      break;
    case "twitterLogin":
      Element = GridSocialButton;
      attributes = { type: "twitter", functional: !preview };
      break;
    default:
      debug.warn("Invalid type: " + type);
      return null;
  }

  // generic attributes
  const generic = {
    rowHeight: rowHeight,
    selected: selected,
    preview: preview,
    fullPreview: fullPreview,
    mini: mini,
    containerStyle: {
      width: "100%",
      height: "100%",
      ...getAlignmentStyle(config.boxAlign),
      ...getSelectedStyle(selected)
    },
    innerStyle: {
      height: "100%",
      width: "100%",
      maxHeight: "100%",
      maxWidth: config.boxMaxWidth ? `${config.boxMaxWidth}px` : ""
    }
  };

  // check if not visible
  if (config.visible === false) {
    if (preview) {
      return <GridElement {...generic} />;
    }
    return null;
  }

  // return element with configuration
  return <Element {...generic} {...config} {...attributes} />;
};

export default LayoutPortalElement;
